/** @jsx jsx */
import * as React from 'react';
import { jsx } from '@compiled/react';
import { isDatePickerSupportEnabled, isDueDateSupportEnabled } from '@helpcenter/feature-flags';
import type { FieldFormProps } from '@helpcenter/field-types';
import { parseISO, isValid as isValidDate, formatISO, parse as dateFnsParse } from 'date-fns';
import { isEmpty } from 'lodash';
import type { WrappedComponentProps } from 'react-intl';
import { injectIntl } from 'react-intl';
import { FieldBase } from 'view/common/fields/field-base';
import messages from 'view/common/messages';
import { css } from '@atlaskit/css';
import DatePicker from '@atlaskit/datetime-picker/date-picker';
import { operationalEvent } from '@atlassian/help-center-common-util/analytics/events';
import dateNow from '@atlassian/help-center-common-util/date-now';
import { format, getWeekStartDay } from '@atlassian/help-center-common-util/date-picker-utils';
import { componentWithCondition } from '@atlassian/help-center-common-util/feature-flags-utils';
import { withControlledFormComponentValue } from '../../with-controlled-form-component-value';
import { withPortalLocale } from '../../with-portal-locale';

export interface DatePickerProps extends FieldFormProps<string>, WrappedComponentProps {
	dateFormat?: string;
	defaultValue?: string;
	firstDay?: number;
	useISO8601WeekNumbers?: boolean;
	locale: string;
	value?: string;
}

const IsoDateFormat = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Processes a date value by handling ISO format and applying necessary transformations
 * @param value - The input date string to process
 *                 Input formats:
 *                 - ISO date format: "YYYY-MM-DD" (e.g., "2023-12-25")
 *                 - JavaScript parseable date strings (e.g., "Dec 25, 2023", "2023/12/25")
 *                 - undefined or null values
 * @returns Processed date string in ISO 8601 format (e.g., "2023-12-25T00:00:00.000Z")
 *          or undefined if the input is invalid or cannot be parsed
 */
export const processDateValue = (value: string | undefined): string | undefined => {
	try {
		if (!value || !isValidDate(new Date(value))) {
			return undefined;
		}

		// Fix for the one-day-off issue with new Date()
		// Link: [DateTime Picker One Day Off Issue](https://hello.atlassian.net/wiki/x/zwTjxw)
		const processedValue = value.match(IsoDateFormat)
			? formatISO(new Date(value.replace(/-/g, '/')))
			: formatISO(new Date(value));

		// Fix for range time error when the string date passed to the date picker is not valid
		return isValidDate(new Date(processedValue)) ? processedValue : undefined;
	} catch (error) {
		operationalEvent({
			action: `processDateValueFailed`,
			actionSubject: 'datePicker',
			attributes: {
				inputValue: value,
				error: error instanceof Error ? error.message : 'unknown',
			},
		});
		return undefined;
	}
};

export const parseInputValueWithDateFormat = (date: string, dateFormat: string): Date => {
	let parsedDate: Date | undefined;
	try {
		parsedDate = dateFnsParse(date, dateFormat, new Date());
	} catch (error) {
		// fire operational event with step information
		operationalEvent({
			action: `dateParseFailed`,
			actionSubject: 'datePicker',
			attributes: {
				dateFormat,
				inputValue: date,
				error: error instanceof Error ? error.message : 'unknown',
			},
		});
	}

	if (parsedDate && isValidDate(parsedDate)) {
		return parsedDate;
	}

	if (isValidDate(new Date(date))) {
		return parseISO(new Date(date).toISOString());
	}

	return parseISO(new Date().toISOString());
};

export class CustomDatePicker extends React.PureComponent<DatePickerProps> {
	onChange = (isoTime: string) => {
		const { onChange } = this.props;
		if (isDueDateSupportEnabled()) {
			onChange &&
				onChange(this.props.id, isoTime, false, {
					rawObject: {},
					rawValue: isoTime,
				});
		} else {
			onChange && onChange(this.props.id, isoTime, false);
		}
	};

	parseDateLabel = (isoTime: string, dateFormat: string): string => {
		return isoTime && dateFormat ? format(parseISO(isoTime), dateFormat) : isoTime;
	};

	render() {
		const {
			id,
			label,
			required,
			defaultValue,
			description,
			errorMessage,
			disabled,
			locale,
			firstDay,
			useISO8601WeekNumbers,
			value,
			intl,
			dateFormat = 'yyyy-MM-dd',
		} = this.props;
		const isInvalid = !isEmpty(errorMessage);
		// Need to refer to id like this.
		// See: https://atlaskit.atlassian.com/packages/core/datetime-picker
		const inputId = `react-select-${id}-input`;
		const defaultDateValue = processDateValue(defaultValue);
		let processedValue = '';
		if (value && isDueDateSupportEnabled()) {
			processedValue = processDateValue(value) ?? '';
		}
		/**
		 * Support both controlled and uncontrolled component behavior.
		 *
		 * When UI Modifications (UIM) feature flag is enabled, the component uses the `value` prop (controlled).
		 * When disabled, it falls back to `defaultValue` prop (uncontrolled).
		 *
		 * This dual behavior will be removed once the UIM feature flag (ditto_uim_-_jsm_support_main)
		 * is fully rolled out and the component becomes fully controlled.
		 */
		const isValueDefined = isDueDateSupportEnabled() && typeof value === 'string';
		const dateFieldValueProps = isValueDefined
			? {
					value: processedValue,
				}
			: {
					defaultValue: defaultDateValue,
				};
		return (
			<FieldBase
				htmlFor={inputId}
				label={label}
				required={required}
				description={description}
				errorMessage={errorMessage}
			>
				<div css={datePickerContainerCss}>
					<DatePicker
						id={inputId}
						name={id}
						locale={locale}
						{...dateFieldValueProps}
						onChange={this.onChange}
						isInvalid={isInvalid}
						isDisabled={!!disabled}
						formatDisplayLabel={this.parseDateLabel}
						placeholder={intl.formatMessage(messages.forExample, {
							text: format(dateNow(), dateFormat),
						})}
						dateFormat={dateFormat}
						weekStartDay={getWeekStartDay(firstDay, useISO8601WeekNumbers)}
						parseInputValue={parseInputValueWithDateFormat}
						aria-describedby={`${inputId}-field-description`}
					/>
				</div>
			</FieldBase>
		);
	}
}

const wrappedDatePicker = withPortalLocale(injectIntl(CustomDatePicker));

export default wrappedDatePicker;

export const ControlledDatePicker = withControlledFormComponentValue(
	(_field: string, value: string) => value,
)(wrappedDatePicker);

export const ConditionalDatepicker = componentWithCondition(
	() => isDatePickerSupportEnabled(),
	ControlledDatePicker,
	wrappedDatePicker,
);

const datePickerContainerCss = css({
	width: '160px', // grid.multiple(20).px
});
